import React from "react";
import { useEffect, useState, useRef, useMemo } from "react";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import { normalizeTaxClassificationForExport } from "../utils/exportFormatting.js";
import { buildUniqueTaxRowKeys } from "../utils/taxRowKeys.js";
import { normalizeSearchText } from "../utils/classificationLabel.js";
import { calculateFifoMatch } from "../utils/fifoUtils";
import FifoDetailRow from "./FifoDetailRow";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t, getTaxCategoryLabel } from "../utils/translations";
import { fmtTokenAmountFixed, fmtEurPrice, fmtEurRow, fmtEurSummary } from "../utils/formatNumbers";
import { resolveV3TaxPlanName } from "../utils/v3taxPlanLabel.js";
import V3TaxPurchaseTypeLabel from "./V3TaxPurchaseTypeLabel.jsx";
import MugshotProSubscriptionTypeLabel, {
  formatMugshotProSubscriptionSubline,
} from "./MugshotProSubscriptionTypeLabel.jsx";
import FourOceanDonationTypeLabel from "./FourOceanDonationTypeLabel.jsx";
import GroncardTypeLabel, {
  isGroncardCashbackRow,
  isGroncardGiftCardPurchase,
} from "./GroncardTypeLabel.jsx";
import MaskedEurCell from "./MaskedEurCell";
import { getPreviewTaxIssueKind, PreviewTaxIssueBadge } from "./PreviewTaxIssue";
import { LayoutDashboard, Grid3X3 } from "lucide-react";
import { pickFiatPrice, pickFiatTotal, pickFiatTaxable, pickSectionTotals, currencySymbolOf } from "../utils/fiatFields";
import {
  shouldMaskPreviewEurRow,
  shouldShowPreviewSeparatorAfterRow,
} from "../utils/previewMode";
import PreviewTableNotice from "./PreviewTableNotice";
import PreviewModeSeparator from "./PreviewModeSeparator";
import UnifiedTableEmptyState, { computeTableEmptyMetrics } from "./UnifiedTableEmptyState.jsx";
import { cn, tableTheme as T, analysisTone } from "./ui/analysisTableTheme.js";
import TaxTreatmentValue from "./TaxTreatmentValue.jsx";
import { rowMatchesTaxYear, resolveDisplayTaxYearLabel } from "../utils/taxYear.js";
import { buildTaxTableSectionData } from "../utils/taxTableSectionData.js";
import { JUICY_TAX_TYPE_FILTER_OPTIONS } from "../config/juicyFinanceClassifications.jsx";
import {
  isSquirtleSquadClass,
  isSquirtleSquadDisposalLike,
  renderSquirtleSquadType,
} from "../config/squirtleSquadClassifications.jsx";
import GasAbstractionFeeLabel from "./GasAbstractionFeeLabel.jsx";
import { getGasAbstractionFeePlainLabel } from "../utils/gasAbstractionFeeLabel.js";
import { getVeBetterDaoNavigatorFeeDisplay } from "../utils/veBetterDaoNavigatorDisplay.js";
import DexAggregatorExecutionCostLabel, {
  getDexAggregatorExecutionCostPlainLabel,
  isDexAggregatorExecutionCostClass,
} from "./DexAggregatorExecutionCostLabel.jsx";
import VaiyansTypeLabel from "./VaiyansTypeLabel.jsx";
import { isVaiyansClassification } from "../utils/vaiyansDisplay.js";
import ScoopUpTypeLabel from "./ScoopUpTypeLabel.jsx";
import { getScoopUpDisplay, isScoopUpClassification } from "../utils/scoopUpDisplay.js";
import VeLotteryTypeLabel from "./VeLotteryTypeLabel.jsx";
import { getVeLotteryDisplay, isVeLotteryClassification } from "../utils/veLotteryDisplay.js";
import {
  B3MO_ICON_SRC,
  B3MO_ICON_CLASS,
  getB3moDisplay,
  isB3moClass,
} from "../utils/b3moDisplay.js";

const HeaderIconBadge = ({ children, className = "" }) => (
  <div className={cn(T.headerMediaBadge, className)}>
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white/85 to-slate-50/80" />
    <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <div className="absolute -top-2 -left-3 h-5 w-12 rotate-12 bg-white/50 blur-md" />
    </div>
    <div className="relative z-10 flex items-center justify-center">
      {children}
    </div>
  </div>
);


// 🚀 Helper-Funktion für Timestamp-Normalisierung
const toSec = (ts) => (Number(ts) > 1e12 ? Math.floor(Number(ts) / 1000) : Number(ts));

// ⚠️ Helper: Prüft ob Fiat-Preis fehlt (robust: nicht nur euroPrice prüfen, sondern auch Flags)
const hasFiatPrice = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
};

const isMissingFiatForTx = (tx) =>
  Boolean(tx?.pricingMissing ?? tx?.priceMissing ?? tx?.missingFiatPrice) ||
  !hasFiatPrice(pickFiatPrice(tx) ?? tx?.euroPrice);

// 🚀 Helper: Nur echte Swap-/Purchase-ähnliche Disposals für die Swap-UI
const isSwapOutLike = (cls) => {
  const c = String(cls || "").toLowerCase();
  return c === "swap_out" || c === "nfbc_purchase" || c === "v3tax_purchase" || c === "mugshot_pro_subscription" || c === "vewin_4ocean_donation" || c === "one_million_pixel_purchase" || c === "vaiyans_vco_game_entry" || c === "velottery_ticket_purchase" || c === "scoopup_puppy_pass_fee" || isDexAggregatorExecutionCostClass(c);
};

// 🏰 Golden Empire Disposals (Spend / Burn Spend) – steuerlich Disposal,
// in der UI aber separat von Swap_out gerendert
const isGoldenEmpireSpendLike = (cls) => {
  const c = String(cls || "").toLowerCase();
  return c === "golden_empire_spend" || c === "golden_empire_burn_spend";
};

// 🍓 JuicyFinance Liquidation: Debt Payment = steuerliche Veräußerung (§23), nicht Other Income
const isJuicyLiquidationDebtPaymentLike = (cls) => {
  return String(cls || "").toLowerCase() === "juicyfinance_liquidation_debt_payment";
};

const isVip180DisposalExpandable = (cls) =>
  isSwapOutLike(cls) ||
  isGroncardGiftCardPurchase(cls) ||
  String(cls || "").toLowerCase() === "gas_abstraction_fee" ||
  isGoldenEmpireSpendLike(cls) ||
  isSquirtleSquadDisposalLike(cls) ||
  isJuicyLiquidationDebtPaymentLike(cls) ||
  String(cls || "").toLowerCase() === "nft_marketplace_purchase_payment";

// Helper: Soll diese Transaktion im PDF alle Details zeigen?
const isImportantFifoTx = (tx) => {
  const type = (tx.type || tx.event || "").toLowerCase();
  const classification = (tx.classification || "").toLowerCase();

  return (
    isSwapOutLike(type) ||
    isSwapOutLike(classification) ||
    type === "gas_abstraction_fee" ||
    classification === "gas_abstraction_fee" ||
    isGoldenEmpireSpendLike(type) ||
    isGoldenEmpireSpendLike(classification) ||
    isSquirtleSquadDisposalLike(type) ||
    isSquirtleSquadDisposalLike(classification) ||
    isJuicyLiquidationDebtPaymentLike(type) ||
    isJuicyLiquidationDebtPaymentLike(classification) ||
    isGroncardGiftCardPurchase(type) ||
    isGroncardGiftCardPurchase(classification) ||
    type === "nft_mint" ||
    classification === "nft_mint" ||
    type === "nft_galaxy_mint" ||
    classification === "nft_galaxy_mint" ||
    type === "nft_marketplace_purchase_payment" ||
    classification === "nft_marketplace_purchase_payment" ||
    type === "sale_payment" ||
    classification === "sale_payment"
  );
};

// 🚀 Helper: Eindeutigen, stabilen Row-Key generieren
const getRowIdentity = (tx) => {
  // best case: Backend liefert eine eindeutige ID
  if (tx.rowId) return String(tx.rowId);
  if (tx.event_id) return `${tx.event_id}`;
  if (tx.logIndex != null && tx.hash) return `${tx.hash}-${tx.logIndex}`;

  // fallback: so breit wie möglich (möglichst stabil und unique)
  return [
    tx.hash || "nohash",
    tx.classification || tx.type || tx.event || "nocls",
    tx.token || "notoken",
    tx.timestamp || "nots",
    tx.amount ?? "noamt",
  ].join("|");
};


function isJuicyFinanceClass(cls) {
  return String(cls || "").trim().toLowerCase().startsWith("juicyfinance_");
}

function isJuicySuppliedToken(token) {
  return String(token || "").toUpperCase().startsWith("JUICY_SUPPLIED:");
}

function extractJuicySuppliedAsset(token) {
  return String(token || "")
    .toUpperCase()
    .replace(/^JUICY_SUPPLIED:/, "")
    .trim();
}

function renderTokenDisplay(token, pdfExportMode = false) {
  const raw = String(token || "").trim();
  if (!raw) return "B3TR";

  if (isJuicySuppliedToken(raw)) {
    const asset = extractJuicySuppliedAsset(raw);
    if (pdfExportMode) {
      return (
        <div className="flex flex-col items-center justify-center leading-tight">
          <span className="font-semibold text-slate-800 print:text-black">JF Supplied</span>
          <span className="font-mono text-[10px] text-slate-600 print:text-black">{asset}</span>
        </div>
      );
    }
    return (
      <div
        className="inline-flex max-w-full flex-col items-center justify-center rounded-xl border border-blue-200 bg-blue-50/70 px-2 py-1 leading-tight shadow-sm"
        title={raw}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
          JF Supplied
        </span>
        <span className="font-mono text-xs font-bold text-slate-800">{asset}</span>
      </div>
    );
  }

  return raw;
}

function toTitleCase(input) {
  return String(input || "")
    .replace(/_/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ")
    .replace(/\bVebetterdao\b/g, "VeBetterDAO");
}

function getJuicyFinanceDisplay(clsRaw, tokenRaw = "") {
  const cls = String(clsRaw || "").trim().toLowerCase();
  const token = String(tokenRaw || "").trim().toUpperCase();

  const assetOpMatch = cls.match(/^juicyfinance_([a-z0-9]+)_(deposit|withdrawal|borrow|repay)$/i);
  if (assetOpMatch) {
    const [, asset, action] = assetOpMatch;
    return {
      title: "JuicyFinance",
      subtitle: `${String(asset || "").toUpperCase()} ${toTitleCase(action)}`,
    };
  }

  switch (cls) {
    case "juicyfinance_b3tr_reward_claim":
      return {
        title: "JuicyFinance",
        subtitle: "B3TR Incentive Reward",
      };
    case "juicyfinance_liquidation_collateral_received":
      return {
        title: "JuicyFinance",
        subtitle: "Liquidation Collateral In",
      };
    case "juicyfinance_liquidation_debt_payment":
      return {
        title: "JuicyFinance",
        subtitle: "Liquidation Debt Payment",
      };
    case "juicyfinance_lending_income":
      return { title: "JuicyFinance", subtitle: token ? `${token} Lending Income` : "Lending Income" };
    case "juicyfinance_borrow_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Principal` : "Borrow Principal" };
    case "juicyfinance_repay_principal":
      return { title: "JuicyFinance", subtitle: token ? `${token} Repay Principal` : "Repay Principal" };
    case "juicyfinance_borrow_interest_paid":
      return { title: "JuicyFinance", subtitle: token ? `${token} Borrow Interest Paid` : "Borrow Interest Paid" };
    case "juicyfinance_unmatched_withdrawal_principal":
      return {
        title: "JuicyFinance",
        subtitle: token
          ? `${token} Withdrawal (Principal unbekannt)`
          : "Withdrawal (Principal unbekannt)",
      };
    default:
      return {
        title: "JuicyFinance",
        subtitle: toTitleCase(cls.replace(/^juicyfinance_/i, "")),
      };
  }
}

function renderJuicyFinanceType(clsRaw, tokenRaw = "") {
  const { title, subtitle } = getJuicyFinanceDisplay(clsRaw, tokenRaw);

  return (
    <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
      <img
        src="/JF.png"
        alt="JuicyFinance"
        className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
        draggable={false}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{title}</div>
        <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function renderBlueAppType({
  icon,
  alt = "",
  title,
  subtitle,
  titleClass = "text-blue-600",
  subtitleClass = "text-blue-700/90",
  iconClass = "h-5 w-5 object-contain flex-shrink-0 mt-0.5",
}) {
  return (
    <div className={`flex items-start gap-2 font-semibold min-w-0 ${titleClass}`}>
      {icon ? (
        <img
          src={icon}
          alt={alt}
          className={iconClass}
          draggable={false}
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : null}
      <div className="min-w-0 leading-tight">
        <div className="whitespace-normal break-words">{title}</div>
        <div className={`whitespace-normal break-words text-[11px] font-medium ${subtitleClass}`}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function normalizeClassKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function isWovStakingRewardsClass(value) {
  const cls = normalizeClassKey(value);
  return (
    cls === "wov_staking_rewards" ||
    cls === "wov_staking_reward" ||
    cls.startsWith("wov_genesis_staking_reward") ||
    cls.startsWith("genesis_staking_reward") ||
    cls.startsWith("world_of_v_staking_reward") ||
    cls.startsWith("worldofv_staking_reward")
  );
}

export default function B3trTaxTable({
  year,
  displayTaxYear,
  b3trDataFromProps = [],
  b3trTotalsFromProps = {},
  onTotalProfitCalculated,
  onDataReady,
  isEditMode = false,
  selectedCountry = 'DE',
  pdfExportMode = false,
  isPreviewMode = false,
  previewMaskedTotalEUR = null,
  previewMaskedTotals = null,
  enableExportCallback = true,
}) {
  const { language } = useLanguage();
  const tone = analysisTone.indigo;
  const primaryTableWrapClass = "v3-primary-table-shell v3-primary-table-shell--indigo";
  const primaryTableHeadClass = "v3-primary-table-head v3-primary-table-head--indigo";

  const cc = String(selectedCountry || "").toUpperCase();
  const isUS = cc === "US" || cc === "USA" || cc.includes("UNITED STATES");
  const totalsMeta = useMemo(
    () => pickSectionTotals(b3trTotalsFromProps || {}, selectedCountry),
    [b3trTotalsFromProps, selectedCountry]
  );
  const currencyCode = totalsMeta.currency;
  const currencySymbol = currencySymbolOf(currencyCode);
  const [data, setData] = useState({ rows: [], totals: { swapProfit: 0, otherIncome: 0, totalTaxable: 0 } });
  const effectiveYear = String(year || ""); // Kein Fallback auf aktuelles Jahr
  const yearLabel = resolveDisplayTaxYearLabel(year, selectedCountry, displayTaxYear);

  const pdfSectionData = useMemo(() => {
    if (!pdfExportMode) return null;
    return buildTaxTableSectionData(
      b3trDataFromProps,
      b3trTotalsFromProps,
      effectiveYear,
      selectedCountry
    );
  }, [pdfExportMode, b3trDataFromProps, b3trTotalsFromProps, effectiveYear, selectedCountry]);

  const activeRows = pdfSectionData?.rows ?? data.rows;
  const activeTotals = pdfSectionData?.totals ?? data.totals;

  const processedRef = useRef(false);
  
  // UI State (nur für Anzeige)
  const [sortKey, setSortKey] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [search, setSearch] = useState("");
  const [tokenFilter, setTokenFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [expandedKey, setExpandedKey] = useState(null);

  const toggleRow = (rowKey) => {
    if (pdfExportMode) return;
    setExpandedKey(prev => (prev === rowKey ? null : rowKey));
  };

  // 🚀 Reset Expand-State bei Filter/Sort-Wechsel
  useEffect(() => {
    setExpandedKey(null);
  }, [typeFilter, tokenFilter, search, sortKey, sortDirection]);

  useEffect(() => {
    
    if (!Array.isArray(b3trDataFromProps)) {
      return;
    }

    // Filter Rows auf das gewählte Jahr (für Tabellen-Anzeige)
    const yearRows = b3trDataFromProps.filter(tx => {
      if (!tx.timestamp) return false;
      if (!effectiveYear) return true; // Wenn kein Jahr gesetzt, zeige alle
      return rowMatchesTaxYear(tx, selectedCountry, Number(effectiveYear));
    });

    const m = pickSectionTotals(b3trTotalsFromProps || {}, selectedCountry);
    const totals = {
      swapProfit: m.disposalNet,
      swapGain: m.disposalGain,
      swapLoss: m.disposalLoss,
      otherIncome: m.otherIncome,
      totalTaxable: m.totalTaxable,
    };

    setData({ rows: yearRows, totals });
    processedRef.current = true;

  }, [b3trDataFromProps, b3trTotalsFromProps, effectiveYear, selectedCountry]);

  // CSV/Export - direkt aus Props
  useEffect(() => {
    if (!onDataReady || isPreviewMode || !enableExportCallback || !b3trDataFromProps || b3trDataFromProps.length === 0) return;
    
    // Filter Rows auf das gewählte Jahr (gleiche Logik wie oben)
    const yearRows = b3trDataFromProps.filter(tx => {
      if (!tx.timestamp) return false;
      if (!effectiveYear) return true;
      return rowMatchesTaxYear(tx, selectedCountry, Number(effectiveYear));
    });
    
    // 🔍 DEBUG: Prüfe B3TR-Daten
                if (yearRows.length > 0) {
                } else {
          }
    
    const csvLocale =
      String(selectedCountry || "DE").toUpperCase().includes("US")
        ? "en-US"
        : "de-DE";

    const exportable = yearRows.map(r => ({
      type: r.classification,
      token: r.token || 'B3TR',
      amount: r.amount,
      fiatPrice: pickFiatPrice(r),
      totalFiat: pickFiatTotal(r),
      taxableProfitFiat: pickFiatTaxable(r) ?? 0,
      euroPrice: pickFiatPrice(r) ?? r.euroPrice ?? null,
      totalEUR: pickFiatTotal(r) ?? r.euroAmount ?? null,
      fifoProfitEUR: pickFiatTaxable(r) ?? r.taxableProfit ?? r.taxableProfitEUR ?? 0,
      hash: r.hash || '',
      date: r.timestamp 
        ? new Date(toSec(r.timestamp) * 1000).toLocaleString(csvLocale, {
            day: "2-digit",
            month: "2-digit", 
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          }).replace(",", "")
        : ''
    }));
    onDataReady(exportable);
  }, [b3trDataFromProps, effectiveYear, selectedCountry, isPreviewMode, onDataReady, enableExportCallback]);

  useEffect(() => {
    if (isPreviewMode) return;
    onTotalProfitCalculated?.({
      swapProfit: activeTotals.swapProfit,
      otherIncome: activeTotals.otherIncome,
      total: activeTotals.totalTaxable
    });
  }, [activeTotals, isPreviewMode]); // ✅ onTotalProfitCalculated entfernt um Endlosschleife zu vermeiden

  // 🎯 NEU: Verfügbare Tokens aus den Daten extrahieren
  const availableTokens = useMemo(() => {
    const tokens = new Set();
    (activeRows || []).forEach(tx => {
      if (tx.token) tokens.add(tx.token.toUpperCase());
    });
    return Array.from(tokens).sort();
  }, [activeRows]);

  // Filterung und Sortierung (wie VetTaxTable)
  const filtered = useMemo(() => {
    let filtered = activeRows || [];
    
    // Suchfilter
    if (search) {
      const searchLower = search.toLowerCase();
      const searchNormalized = normalizeSearchText(search);
      filtered = filtered.filter(tx => 
        (tx.classification || '').toLowerCase().includes(searchLower) ||
        (searchNormalized !== '' &&
          normalizeSearchText(tx.classification || tx.type || '').includes(searchNormalized)) ||
        (tx.hash || '').toLowerCase().includes(searchLower) ||
        (tx.token || '').toLowerCase().includes(searchLower) ||
        (tx.amount || 0).toString().includes(searchLower) ||
        (tx.euroPrice || 0).toString().includes(searchLower) ||
        (tx.euroAmount || 0).toString().includes(searchLower) ||
        (tx.taxableProfitEUR ?? tx.taxableProfit ?? 0).toString().includes(searchLower) ||
        (tx.timestamp ? new Date(toSec(tx.timestamp) * 1000).toLocaleDateString() : '').includes(searchLower)
      );
    }
    
    // Token-Filter
    if (tokenFilter !== "ALL") {
      filtered = filtered.filter(tx => (tx.token || '').toUpperCase() === tokenFilter);
    }
    
    // Typ-Filter
    if (typeFilter !== "ALL") {
      const selectedType = normalizeClassKey(typeFilter);
      filtered = filtered.filter((tx) => {
        const cls = normalizeClassKey(tx.classification || tx.type || "");
        if (isWovStakingRewardsClass(selectedType)) {
          return isWovStakingRewardsClass(cls);
        }
        if (
          selectedType === "x2earn_reward_claim" ||
          selectedType === "x2earn_reward" ||
          selectedType === "x2earn_reward_distribution" ||
          selectedType === "airdrop"
        ) {
          return (
            cls === "airdrop" ||
            cls === "x2earn_reward" ||
            cls === "x2earn_reward_claim" ||
            cls === "x2earn_reward_distribution" ||
            cls.includes("x2earn")
          );
        }
        if (
          selectedType === "b3mo" ||
          selectedType.startsWith("b3mo_")
        ) {
          if (selectedType === "b3mo") {
            return (
              isB3moClass(cls) ||
              String(tx.x2earnRewardKind || "").toLowerCase() === "b3mo_quest" ||
              String(tx.subApplication || "").toLowerCase().includes("b3mo")
            );
          }
          return cls === selectedType;
        }
        return cls === selectedType;
      });
    }
    
    return filtered;
  }, [activeRows, search, tokenFilter, typeFilter]);

  // Sortierung (wie VetTaxTable)
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    
    return [...filtered].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      
      // Spezielle Behandlung für verschiedene Datentypen
      if (sortKey === 'timestamp') {
        aVal = toSec(aVal);
        bVal = toSec(bVal);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      } else {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDirection]);

  const rowKeys = useMemo(
    () => buildUniqueTaxRowKeys(sorted, getRowIdentity),
    [sorted]
  );

  const emptyMetrics = useMemo(
    () =>
      computeTableEmptyMetrics({
        allRows: b3trDataFromProps,
        yearRows: activeRows,
        filteredRows: filtered,
        effectiveYear,
      }),
    [b3trDataFromProps, activeRows, filtered, effectiveYear]
  );


  const otherIncomeHint = useMemo(() => {
    const set = new Set((sorted || []).map(tx => normalizeClassKey(tx.classification || tx.type || "")));
    const parts = [];
    if (set.has('airdrop')) parts.push(language === 'en' ? 'Airdrops' : 'Airdrops');
    if (set.has('governance_reward')) parts.push(language === 'en' ? 'Governance rewards' : 'Governance-Rewards');
    if (set.has('vexchange_farm_rewards')) parts.push('Vexchange');
    if (set.has('mva_staking_rewards')) parts.push('MVA');
    if ([...set].some(isWovStakingRewardsClass)) parts.push("World of V");
    if (set.has('golden_empire_reward')) parts.push('Golden Empire');
    if (set.has('vesea_nft_staking_rewards')) parts.push('VeSea');
    if (set.has('union_nft_holder_rewards')) parts.push('UNION');
    return parts.length ? parts.join(' / ') : (language === 'en' ? 'Other income' : 'Sonstige Einkünfte');
  }, [sorted, language]);

  const totals = activeTotals || { swapProfit: 0, otherIncome: 0, totalTaxable: 0 };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const getArrow = (key) => {
    if (key !== sortKey) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const PdfTokenRowHeader = () => (
    <tr className="pdf-token-row-head">
      <th className={cn(T.th, "v3-pdf-type-col text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("type", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("txHashColumn", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>Token</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("amount", language)}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("price", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("total", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
          <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
        </span>
      </th>
      <th className={cn(T.th, "text-center")}>
        <span className={T.thInnerCenter}>
          <span className={cn(T.thLabel, "v3-th-label-fix")}>{pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}</span>
        </span>
      </th>
    </tr>
  );

  const renderDesktopColgroup = () => (
    <colgroup>
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "26.3%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
      <col style={{ width: "10.5%" }} />
    </colgroup>
  );

  const renderDesktopThead = () => (
    <thead className={cn(T.thead, primaryTableHeadClass)}>
      <tr>
        <th
          className={cn(T.th, "v3-pdf-type-col w-[10.5%] print:w-[10.5%] cursor-pointer")}
          onClick={() => handleSort("classification")}
        >
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("type", language)}
              {getArrow("classification")}
            </span>
          </span>
        </th>
        <th className={cn(T.th, "w-[26.3%] print:w-[26.3%] cursor-pointer")} onClick={() => handleSort("hash")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("txHashColumn", language)}
              {getArrow("hash")}
            </span>
          </span>
        </th>
        <th className={cn(T.th, "text-center w-[10.5%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("token", language)}</span>
          </span>
        </th>
        <th className={cn(T.th, "text-center w-[10.5%] cursor-pointer")} onClick={() => handleSort("amount")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("amount", language)}
              {getArrow("amount")}
            </span>
          </span>
        </th>
        <th className={cn(T.th, "text-center w-[10.5%] cursor-pointer")} onClick={() => handleSort("euroPrice")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("price", language)}
              {getArrow("euroPrice")}
            </span>
            <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
          </span>
        </th>
        <th className={cn(T.th, "text-center w-[10.5%] cursor-pointer")} onClick={() => handleSort("euroAmount")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {t("total", language)}
              {getArrow("euroAmount")}
            </span>
            <span className={cn(T.thSub, "v3-th-sub-fix")}>{currencyCode}</span>
          </span>
        </th>
        <th className={cn(T.th, "text-center w-[10.5%]")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>{t("taxableProfitShort", language)}</span>
            <span className={cn(T.thSub, "v3-th-sub-fix")}>({currencyCode})</span>
          </span>
        </th>
        <th className={cn(T.th, "text-center w-[10.5%] cursor-pointer")} onClick={() => handleSort("timestamp")}>
          <span className={T.thInnerCenter}>
            <span className={cn(T.thLabel, "v3-th-label-fix")}>
              {pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}
              {getArrow("timestamp")}
            </span>
          </span>
        </th>
      </tr>
    </thead>
  );

  const renderDesktopTableFooter = () => (
    <>
      <tr className={cn("font-semibold border-t", tone.footerPrimary, "print:bg-gray-200 print:text-black")}>
        <td colSpan={6} className="px-6 py-4 text-right print:text-black">
          {getTaxCategoryLabel("disposal", selectedCountry, language)} ({t("swaps", language)}) –{" "}
          {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
        </td>
        <td className="px-6 py-4 font-mono print:text-black">
          {isPreviewMode ? (
            <MaskedEurCell
              currencySymbol={currencySymbol}
              currencyCode={currencyCode}
              language={language}
              value={previewMaskedTotals?.disposal || `${currencySymbol} **.**`}
              fullWidth
            />
          ) : (
            <>
              {currencySymbol} {fmtEurSummary(totals.swapProfit)}
            </>
          )}
        </td>
        <td className="px-6 py-4" />
      </tr>
      <tr className={cn("font-semibold border-t", tone.footerSecondary, "print:bg-gray-200 print:text-black")}>
        <td colSpan={6} className="px-6 py-4 text-right print:text-black">
          {getTaxCategoryLabel("other", selectedCountry, language)} ({otherIncomeHint}) –{" "}
          {language === "en" ? `Sum ${yearLabel}` : `Summe Steuerjahr ${yearLabel}`}:
        </td>
        <td className="px-6 py-4 font-mono print:text-black">
          {isPreviewMode ? (
            <MaskedEurCell
              currencySymbol={currencySymbol}
              currencyCode={currencyCode}
              language={language}
              value={previewMaskedTotals?.other || `${currencySymbol} **.**`}
              fullWidth
            />
          ) : (
            <>
              {currencySymbol} {fmtEurSummary(totals.otherIncome)}
            </>
          )}
        </td>
        <td className="px-6 py-4" />
      </tr>
    </>
  );

  const renderB3trDesktopBlock = (tx, index, { virtualMode = false } = {}) => {
const maskRowEur = shouldMaskPreviewEurRow(index, sorted.length, isPreviewMode);
              const rowKey = rowKeys[index] ?? getRowIdentity(tx);
              const isExpanded = expandedKey === rowKey;
              
              const cls =
                normalizeTaxClassificationForExport(
                  tx.classification || tx.type,
                  tx.token || tx.tokenSymbol || tx.asset
                ) ||
                String(tx.classification || tx.type || "").toLowerCase();
              const isSwapOutLikeResult = isSwapOutLike(cls);
              const isGasAbstractionFee = cls === "gas_abstraction_fee";
              const isDexExecutionCost = isDexAggregatorExecutionCostClass(cls);
              const isGoldenEmpireSpendLikeResult = isGoldenEmpireSpendLike(cls);
              const isGroncardPurchaseResult = isGroncardGiftCardPurchase(cls);
              const isSquirtleSquadDisposalLikeResult = isSquirtleSquadDisposalLike(cls);
              const isJuicyLiquidationDebtPaymentResult = isJuicyLiquidationDebtPaymentLike(cls);
              const isVaiyansResult = isVaiyansClassification(cls);
              const isScoopUpResult = isScoopUpClassification(cls);
              const isVeLotteryResult = isVeLotteryClassification(cls);
              const isExpandableDisposal = isVip180DisposalExpandable(cls);
              // Vaiyans rows (Game Entry disposals + Leaderboard income) must
              // stay expandable like Swap, even when income has no fifoLegs yet.
              const isVaiyansExpandable = isVaiyansResult;
              const isScoopUpExpandable = isScoopUpResult;
              const isVeLotteryExpandable = isVeLotteryResult;

              const hasFifoDetails =
                (Array.isArray(tx.fifoLegs) && tx.fifoLegs.length > 0) ||
                (Array.isArray(tx.matchedInflows) && tx.matchedInflows.length > 0);

              const missing = isMissingFiatForTx(tx);
              const previewIssueKind = getPreviewTaxIssueKind(tx);
              /** Fehlender Kurs: immer eine Zeile (colSpan 3), nie drei Masked/Hint-Zellen — Preview & Premium */
              const mergedIssueKind =
                missing ? (previewIssueKind === "manual" ? "manual" : "price") : previewIssueKind;
              const showMergedFinancialBlock =
                Boolean(mergedIssueKind) && (missing || isPreviewMode);

              const shouldShowButton =
                hasFifoDetails ||
                isExpandableDisposal ||
                isVaiyansExpandable ||
                isScoopUpExpandable ||
                isVeLotteryExpandable;
              
              const showDetails = pdfExportMode
                ? hasFifoDetails && isImportantFifoTx(tx)
                : isExpanded &&
                  (hasFifoDetails || isExpandableDisposal || isVaiyansExpandable);
              
              const getLabel = () => {
                if (isDexExecutionCost) return getDexAggregatorExecutionCostPlainLabel(tx);
                if (cls === "nfbc_purchase") return language === "de" ? "NFBC Kauf" : "NFBC Purchase";
                if (cls === "mugshot_pro_subscription") {
                  return `Mugshot · ${formatMugshotProSubscriptionSubline(tx, language)}`;
                }
                if (cls === "vewin_4ocean_donation") {
                  return language === "de" ? "4Ocean · Spende" : "4Ocean · Donation";
                }
                if (cls === "one_million_pixel_purchase") {
                  return "1Million Pixel – Pixel Purchase";
                }
                if (cls === "v3tax_purchase") {
                  const plan = resolveV3TaxPlanName(tx);
                  if (plan) return `V3Tax ${plan}`;
                  return language === "de" ? "V3Tax Kauf" : "V3Tax purchase";
                }
                if (cls === "golden_empire_spend") return "Golden Empire Spend";
                if (cls === "golden_empire_burn_spend") return "Golden Empire Burn Spend";
                if (cls === "vaiyans_vco_game_entry") {
                  return "Vaiyans Crypto Oracle – Game Entry";
                }
                const scoopUpLabel = getScoopUpDisplay(cls);
                if (scoopUpLabel) {
                  return `${scoopUpLabel.title} – ${scoopUpLabel.subtitle}`;
                }
                const veLotteryLabel = getVeLotteryDisplay(cls);
                if (veLotteryLabel) {
                  return `${veLotteryLabel.title} – ${veLotteryLabel.subtitle}`;
                }
                if (cls === "squad_game_spend") {
                  return language === "de"
                    ? "Squirtle Squad – Game Spend"
                    : "Squirtle Squad – Game Spend";
                }
                if (cls === "squad_nft_upgrade_spend") {
                  return language === "de"
                    ? "Squirtle Squad – NFT Upgrade"
                    : "Squirtle Squad – NFT Upgrade";
                }
                if (cls === "juicyfinance_liquidation_debt_payment") {
                  return language === "de"
                    ? "JuicyFinance Liquidation Debt Payment"
                    : "JuicyFinance Liquidation Debt Payment";
                }
                if (isGroncardGiftCardPurchase(cls)) {
                  return language === "de"
                    ? "Groncard · Gutschein-Kauf"
                    : "Groncard · Gift Card Purchase";
                }
                return "Swap_out";
              };
              const pdfTypeLabel = isJuicyLiquidationDebtPaymentResult
                ? getLabel()
                : isGroncardPurchaseResult
                  ? getLabel()
                : isSwapOutLikeResult
                ? getLabel()
                : cls === "gas_abstraction_fee"
                  ? getGasAbstractionFeePlainLabel(language)
                : cls === "airdrop" ||
                    cls === "x2earn_reward" ||
                    cls === "x2earn_reward_claim" ||
                    cls === "x2earn_reward_distribution"
                  ? "X2Earn Reward"
                  : toTitleCase(tx.classification || tx.type || "Unknown");

              const rowContent = (
                <>
                  {pdfExportMode && showDetails ? <PdfTokenRowHeader /> : null}
                  <tr
                    className={[
                      "group pdf-tx-main-row border-b transition-colors duration-150",
                      missing
                        ? "bg-amber-50/40 hover:bg-amber-50/70"
                        : "hover:bg-slate-50/80",
                    ].join(" ")}
                  >
                    <td
                      className={cn(
                        T.td,
                        "v3-pdf-type-col min-w-0 align-top whitespace-normal break-words print:text-black"
                      )}
                    >
                      {pdfExportMode ? (
                        isVaiyansResult ? (
                          <VaiyansTypeLabel classification={cls} />
                        ) : isScoopUpResult ? (
                          <ScoopUpTypeLabel classification={cls} />
                        ) : isVeLotteryResult ? (
                          <VeLotteryTypeLabel classification={cls} />
                        ) : isGroncardPurchaseResult ? (
                          <GroncardTypeLabel
                            tx={tx}
                            classification={cls}
                            language={language}
                            titleClassName="text-gray-700 font-medium"
                            subtitleClassName="text-[11px] font-medium text-gray-700/90"
                          />
                        ) : isGroncardCashbackRow(tx, cls) ? (
                          <GroncardTypeLabel
                            tx={tx}
                            classification={cls}
                            language={language}
                            titleClassName="text-gray-700 font-medium"
                            subtitleClassName="text-[11px] font-medium text-gray-700/90"
                          />
                        ) : cls === "airdrop" ||
                        cls === "x2earn_reward" ||
                        cls === "x2earn_reward_claim" ||
                        cls === "x2earn_reward_distribution" ? (
                          <div className="block w-full min-w-0 leading-tight font-medium text-gray-700">
                            <div className="whitespace-normal break-words">X2Earn Reward</div>
                            <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
                              Claim
                            </div>
                          </div>
                        ) : (
                          <span className="block w-full min-w-0 whitespace-normal break-words font-medium text-gray-700">
                            {pdfTypeLabel}
                          </span>
                        )
                      ) : isVaiyansResult ? (
                        shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-left hover:opacity-90 focus:outline-none"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <VaiyansTypeLabel
                              classification={cls}
                              leading={isExpanded ? "▼" : "▶"}
                            />
                          </button>
                        ) : (
                          <VaiyansTypeLabel classification={cls} />
                        )
                      ) : isScoopUpResult ? (
                        shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-left hover:opacity-90 focus:outline-none"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <ScoopUpTypeLabel
                              classification={cls}
                              leading={isExpanded ? "▼" : "▶"}
                            />
                          </button>
                        ) : (
                          <ScoopUpTypeLabel classification={cls} />
                        )
                      ) : isVeLotteryResult ? (
                        shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-left hover:opacity-90 focus:outline-none"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <VeLotteryTypeLabel
                              classification={cls}
                              leading={isExpanded ? "▼" : "▶"}
                            />
                          </button>
                        ) : (
                          <VeLotteryTypeLabel classification={cls} />
                        )
                      ) : isGasAbstractionFee ? (
                        shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="block w-full min-w-0 whitespace-normal [overflow-wrap:anywhere] text-left hover:opacity-90 focus:outline-none"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <span className="flex w-full min-w-0 items-start gap-1">
                              <span className="mt-0.5 flex-shrink-0" aria-hidden>
                                {isExpanded ? "\u25bc" : "\u25b6"}
                              </span>
                              <GasAbstractionFeeLabel
                                language={language}
                                token={tx.token || tx.tokenSymbol || tx.asset || "B3TR"}
                                className="min-w-0 whitespace-normal [overflow-wrap:anywhere]"
                              />
                            </span>
                          </button>
                        ) : (
                          <GasAbstractionFeeLabel
                            language={language}
                            token={tx.token || tx.tokenSymbol || tx.asset || "B3TR"}
                            className="min-w-0 whitespace-normal [overflow-wrap:anywhere]"
                          />
                        )
                      ) : isJuicyLiquidationDebtPaymentResult ? (
                        shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-blue-600 font-semibold hover:text-blue-800 focus:outline-none flex items-start gap-2 min-w-0"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <span className="mt-0.5">{isExpanded ? "▼" : "▶"}</span>
                            {renderJuicyFinanceType(cls, tx.token || "B3TR")}
                          </button>
                        ) : (
                          <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                            <span className="mt-0.5">▶</span>
                            {renderJuicyFinanceType(cls, tx.token || "B3TR")}
                          </div>
                        )
                      ) : isSquirtleSquadDisposalLikeResult ? (
                        shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-blue-600 font-semibold hover:text-blue-800 focus:outline-none flex items-start gap-2 min-w-0"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <span className="mt-0.5">{isExpanded ? "▼" : "▶"}</span>
                            {renderSquirtleSquadType(cls)}
                          </button>
                        ) : (
                          <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                            <span className="mt-0.5">▶</span>
                            {renderSquirtleSquadType(cls)}
                          </div>
                        )
                      ) : isGoldenEmpireSpendLikeResult ? (
                        pdfExportMode ? (
                          <div className="flex items-start gap-2 text-gray-700 font-medium min-w-0">
                            <img
                              src="/GE.jpg"
                              alt=""
                              className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                              draggable={false}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="min-w-0 leading-tight">
                              <div className="whitespace-normal break-words">Golden Empire</div>
                              <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
                                {cls === "golden_empire_burn_spend" ? "Burn Spend" : "Spend"}
                              </div>
                            </div>
                          </div>
                        ) : shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-blue-600 font-semibold hover:text-blue-800 focus:outline-none flex items-start gap-2 min-w-0"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <span className="mt-0.5">{isExpanded ? "▼" : "▶"}</span>
                            <img
                              src="/GE.jpg"
                              alt=""
                              className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                              draggable={false}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="min-w-0 leading-tight text-left">
                              <div className="whitespace-normal break-words">Golden Empire</div>
                              <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                {cls === "golden_empire_burn_spend" ? "Burn Spend" : "Spend"}
                              </div>
                            </div>
                          </button>
                        ) : (
                          <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                            <span className="mt-0.5">▶</span>
                            <img
                              src="/GE.jpg"
                              alt=""
                              className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                              draggable={false}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="min-w-0 leading-tight">
                              <div className="whitespace-normal break-words">Golden Empire</div>
                              <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                {cls === "golden_empire_burn_spend" ? "Burn Spend" : "Spend"}
                              </div>
                            </div>
                          </div>
                        )
                      ) : isGroncardPurchaseResult ? (
                        pdfExportMode ? (
                          <GroncardTypeLabel
                            tx={tx}
                            classification={cls}
                            language={language}
                            titleClassName="text-gray-700 font-medium"
                            subtitleClassName="text-[11px] font-medium text-gray-700/90"
                          />
                        ) : shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-left hover:opacity-90 focus:outline-none"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <GroncardTypeLabel
                              tx={tx}
                              classification={cls}
                              language={language}
                              leading={isExpanded ? "▼" : "▶"}
                            />
                          </button>
                        ) : (
                          <GroncardTypeLabel
                            tx={tx}
                            classification={cls}
                            language={language}
                            leading="▶"
                          />
                        )
                      ) : isSwapOutLikeResult ? (
                        isDexExecutionCost ? (
                          pdfExportMode ? (
                            <span className="text-gray-700 font-medium">{getLabel()}</span>
                          ) : shouldShowButton ? (
                            <button
                              onClick={() => toggleRow(rowKey)}
                              className="text-left hover:opacity-90 focus:outline-none flex items-start gap-1"
                              disabled={pdfExportMode}
                              type="button"
                            >
                              <span className="mt-0.5">{isExpanded ? "▼" : "▶"}</span>
                              <DexAggregatorExecutionCostLabel tx={tx} />
                            </button>
                          ) : (
                            <DexAggregatorExecutionCostLabel tx={tx} />
                          )
                        ) : cls === "vewin_4ocean_donation" ? (
                          pdfExportMode ? (
                            <FourOceanDonationTypeLabel language={language} />
                          ) : shouldShowButton ? (
                            <button
                              onClick={() => toggleRow(rowKey)}
                              className="text-left hover:opacity-90 focus:outline-none"
                              disabled={pdfExportMode}
                              type="button"
                            >
                              <FourOceanDonationTypeLabel
                                language={language}
                                leading={isExpanded ? "▼" : "▶"}
                              />
                            </button>
                          ) : (
                            <FourOceanDonationTypeLabel language={language} leading="▶" />
                          )
                        ) : cls === "one_million_pixel_purchase" ? (
                          pdfExportMode ? (
                            <span className="text-blue-600 font-semibold">
                              1Million Pixel – Pixel Purchase
                            </span>
                          ) : shouldShowButton ? (
                            <button
                              onClick={() => toggleRow(rowKey)}
                              className="text-left hover:opacity-90 focus:outline-none flex items-start gap-1"
                              disabled={pdfExportMode}
                              type="button"
                            >
                              <span className="mt-0.5">{isExpanded ? "▼" : "▶"}</span>
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <Grid3X3
                                  className="h-5 w-5 flex-shrink-0 mt-0.5"
                                  aria-hidden="true"
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">
                                    1Million Pixel
                                  </div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Pixel Purchase
                                  </div>
                                </div>
                              </div>
                            </button>
                          ) : (
                            <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                              <Grid3X3
                                className="h-5 w-5 flex-shrink-0 mt-0.5"
                                aria-hidden="true"
                              />
                              <div className="min-w-0 leading-tight">
                                <div className="whitespace-normal break-words">
                                  1Million Pixel
                                </div>
                                <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                  Pixel Purchase
                                </div>
                              </div>
                            </div>
                          )
                        ) : cls === "mugshot_pro_subscription" ? (
                          pdfExportMode ? (
                            <span className="text-gray-700 font-medium">{getLabel()}</span>
                          ) : shouldShowButton ? (
                            <button
                              onClick={() => toggleRow(rowKey)}
                              className="text-left hover:opacity-90 focus:outline-none"
                              disabled={pdfExportMode}
                              type="button"
                            >
                              <MugshotProSubscriptionTypeLabel
                                tx={tx}
                                language={language}
                                leading={isExpanded ? "▼" : "▶"}
                              />
                            </button>
                          ) : (
                            <MugshotProSubscriptionTypeLabel tx={tx} language={language} leading="▶" />
                          )
                        ) : cls === "v3tax_purchase" ? (
                          pdfExportMode ? (
                            <span className="text-gray-700 font-medium">{getLabel()}</span>
                          ) : shouldShowButton ? (
                            <button
                              onClick={() => toggleRow(rowKey)}
                              className="text-left hover:opacity-90 focus:outline-none"
                              disabled={pdfExportMode}
                              type="button"
                            >
                              <V3TaxPurchaseTypeLabel
                                tx={tx}
                                language={language}
                                leading={isExpanded ? "▼" : "▶"}
                              />
                            </button>
                          ) : (
                            <V3TaxPurchaseTypeLabel tx={tx} language={language} leading="▶" />
                          )
                        ) : pdfExportMode ? (
                          <span className="text-gray-700 font-medium">{getLabel()}</span>
                        ) : shouldShowButton ? (
                          <button
                            onClick={() => toggleRow(rowKey)}
                            className="text-blue-600 font-semibold hover:text-blue-800 focus:outline-none flex items-center gap-1"
                            disabled={pdfExportMode}
                            type="button"
                          >
                            <span>{isExpanded ? "▼" : "▶"}</span>
                            <span>{getLabel()}</span>
                          </button>
                        ) : (
                          <span className="text-blue-600 font-semibold inline-flex items-center gap-1">
                            <span>▶</span>
                            <span>{getLabel()}</span>
                          </span>
                        )
                      ) : (
                        (() => {
                          const cls = (tx.classification || tx.type || '').toLowerCase();
                          if (isJuicyFinanceClass(cls)) {
                            return renderJuicyFinanceType(cls, tx.token || "B3TR");
                          }
                          if (cls === "gas_abstraction_fee") {
                            return (
                              <GasAbstractionFeeLabel
                                language={language}
                                token={tx.token || tx.tokenSymbol || tx.asset || "B3TR"}
                              />
                            );
                          }
                          if (cls === 'vexchange_farm_rewards') {
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <img
                                  src="/Vexchange.png"
                                  alt=""
                                  className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                                  draggable={false}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">Vexchange</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Farm Rewards
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (isGroncardCashbackRow(tx, cls)) {
                            return (
                              <GroncardTypeLabel
                                tx={tx}
                                classification={cls}
                                language={language}
                                titleClassName="text-gray-800 font-normal"
                                subtitleClassName="text-[11px] font-medium text-gray-700/90"
                              />
                            );
                          }

                          if (
                            cls === "airdrop" ||
                            cls === "x2earn_reward" ||
                            cls === "x2earn_reward_claim" ||
                            cls === "x2earn_reward_distribution"
                          ) {
                            const b3moDisplay = getB3moDisplay(cls, tx);
                            if (b3moDisplay) {
                              return renderBlueAppType({
                                icon: B3MO_ICON_SRC,
                                iconClass: B3MO_ICON_CLASS,
                                alt: 'B3MO',
                                title: b3moDisplay.title,
                                subtitle: b3moDisplay.subtitle,
                              });
                            }
                            return (
                              <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">X2Earn Reward</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
                                    Claim
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          if (cls === 'safehaven_staking_rewards') {
                            return renderBlueAppType({
                              icon: '/sha.png',
                              alt: 'Safehaven',
                              title: 'Safehaven',
                              subtitle: 'Staking Rewards',
                            });
                          }

                          if (cls === 'sagaz_reward_claim') {
                            return renderBlueAppType({
                              icon: '/sagaz.jpg',
                              alt: 'Sagaz',
                              title: 'Sagaz',
                              subtitle: 'Reward Claim',
                            });
                          }

                          if (isSquirtleSquadClass(cls)) {
                            return renderSquirtleSquadType(cls);
                          }

                          if (cls === 'vfox_nft_holder_reward') {
                            return renderBlueAppType({
                              icon: '/VFA.jpg',
                              alt: 'VFA',
                              title: 'VFA',
                              subtitle: 'NFT Holder Reward',
                            });
                          }

                          if (cls === 'vesea_nft_staking_rewards') {
                            return renderBlueAppType({
                              icon: '/VSEA.png',
                              alt: 'VeSea',
                              title: 'VeSea',
                              subtitle: 'NFT Staking Rewards',
                            });
                          }

                          if (cls === 'union_nft_holder_rewards') {
                            return renderBlueAppType({
                              icon: '/UNION.png',
                              alt: 'UNION',
                              title: 'UNION',
                              subtitle: 'NFT Holder Rewards',
                            });
                          }

                          if (cls === 'vimworld_veed_reward_distribution') {
                            return renderBlueAppType({
                              icon: '/VIM.png',
                              alt: 'VIMworld',
                              title: 'VIMworld',
                              subtitle: 'Reward Distribution',
                            });
                          }
                          const navigatorFeeDisplay = getVeBetterDaoNavigatorFeeDisplay(
                            tx,
                            cls,
                            language
                          );
                          if (navigatorFeeDisplay) {
                            return renderBlueAppType({
                              icon: '/b3tr-icon.png',
                              alt: 'B3TR',
                              title: navigatorFeeDisplay.title,
                              subtitle: navigatorFeeDisplay.subtitle,
                              titleClass: 'text-green-600',
                              subtitleClass: 'text-green-700/90',
                            });
                          }
                          if (cls === 'governance_reward') {
                            return (
                              <div className="flex items-start gap-2 text-gray-800 font-normal min-w-0">
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">Governance Voting</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-gray-700/90">
                                    Reward
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (isWovStakingRewardsClass(cls)) {
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <img
                                  src="/wov.png"
                                  alt=""
                                  className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                                  draggable={false}
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">World of V</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Staking Rewards
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (cls === 'betterswap_pool2earn_rewards') {
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <img
                                  src="/Betterswap.png"
                                  alt=""
                                  className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                                  draggable={false}
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">Betterswap</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Pool2earn Rewards
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (cls === 'mva_staking_rewards') {
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <img
                                  src="/MVA.jpg"
                                  alt=""
                                  className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                                  draggable={false}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">MVA</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Staking Rewards
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (cls === 'listen2earn_rewards') {
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <img
                                  src="/nfbc.png"
                                  alt="NFBC"
                                  className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                                  draggable={false}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">NFBC</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Listen2earn Rewards
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (cls === 'wanchain_bridge_to_earn_reward') {
                            return renderBlueAppType({
                              icon: '/WAN.png',
                              alt: 'Wanchain',
                              title: 'Wanchain Bridge-to-Earn Reward',
                              subtitle: 'Cross-Chain Task Reward',
                            });
                          }
                          if (cls === 'bangzboardz_staking_reward') {
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">BangzBoardz Staking Reward</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    Paladins / NFT Staking
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          if (cls === 'goatz_milk_nft_holder_reward') {
                            return renderBlueAppType({
                              icon: '/goatz.png',
                              alt: 'Goatz Club',
                              title: 'Goatz Club',
                              subtitle: 'MILK NFT Holder Reward',
                            });
                          }
                          const b3moDisplay = getB3moDisplay(cls, tx);
                          if (b3moDisplay) {
                            return renderBlueAppType({
                              icon: B3MO_ICON_SRC,
                              iconClass: B3MO_ICON_CLASS,
                              alt: 'B3MO',
                              title: b3moDisplay.title,
                              subtitle: b3moDisplay.subtitle,
                            });
                          }
                          if (
                            cls === 'golden_empire_reward' ||
                            cls === 'golden_empire_spend' ||
                            cls === 'golden_empire_burn_spend'
                          ) {
                            let actionLabel = 'Interaction';
                            if (cls === 'golden_empire_reward') actionLabel = 'Rewards';
                            else if (cls === 'golden_empire_spend') actionLabel = 'Spend';
                            else if (cls === 'golden_empire_burn_spend') actionLabel = 'Burn Spend';
                            return (
                              <div className="flex items-start gap-2 text-blue-600 font-semibold min-w-0">
                                <img
                                  src="/GE.jpg"
                                  alt=""
                                  className="h-5 w-5 object-contain flex-shrink-0 mt-0.5"
                                  draggable={false}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div className="min-w-0 leading-tight">
                                  <div className="whitespace-normal break-words">Golden Empire</div>
                                  <div className="whitespace-normal break-words text-[11px] font-medium text-blue-700/90">
                                    {actionLabel}
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return <span className="text-gray-800">{toTitleCase(tx.classification || tx.type || 'Unknown')}</span>;
                        })()
                      )}
                    </td>
                    <td className={cn(T.td, T.mono, "break-all whitespace-normal text-slate-700 print:text-black")}>
                      {tx.hash || tx.txid || tx.event_id || '—'}
                      {pdfExportMode && (tx.clause_index != null || tx.clauseIndex != null || tx.event_index != null || tx.eventIndex != null || tx.log_index != null || tx.logIndex != null) ? (
                        <div className="text-[10px] text-slate-500 print:text-slate-600 mt-0.5">
                          {`Event: C${tx.clause_index ?? tx.clauseIndex ?? "–"} / E${tx.event_index ?? tx.eventIndex ?? tx.log_index ?? tx.logIndex ?? "–"}`}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-800">{renderTokenDisplay(tx.token, pdfExportMode)}</td>
                    <td className="px-6 py-4 text-center font-mono text-gray-900">{fmtTokenAmountFixed(tx.amount || 0, 6)}</td>
                    {showMergedFinancialBlock ? (
                      <td colSpan={3} className="px-6 py-4 align-middle">
                        <div className="flex justify-center">
                          <PreviewTaxIssueBadge
                            language={language}
                            issueKind={mergedIssueKind}
                            align="center"
                            compact={false}
                          />
                        </div>
                      </td>
                    ) : (
                      <>
                        <td className="px-6 py-4 text-center font-mono text-gray-900">
                          {maskRowEur ? (
                            <MaskedEurCell
                              currencySymbol={currencySymbol}
                              currencyCode={currencyCode}
                              language={language}
                              fullWidth
                            />
                          ) : (
                            <>{currencySymbol} {fmtEurPrice(pickFiatPrice(tx) ?? 0)}</>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center font-mono text-gray-900">
                          {maskRowEur ? (
                            <MaskedEurCell
                              currencySymbol={currencySymbol}
                              currencyCode={currencyCode}
                              language={language}
                              fullWidth
                            />
                          ) : (
                            <>{currencySymbol} {fmtEurRow(pickFiatTotal(tx) ?? 0)}</>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center text-gray-900">
                          {maskRowEur ? (
                            <MaskedEurCell
                              currencySymbol={currencySymbol}
                              currencyCode={currencyCode}
                              language={language}
                              fullWidth
                            />
                          ) : (
                            <TaxTreatmentValue
                              row={tx}
                              language={language}
                              selectedCountry={selectedCountry}
                              formattedValue={`${currencySymbol} ${fmtEurRow(
                                pickFiatTaxable(tx) ?? 0
                              )}`}
                            />
                          )}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 text-center text-gray-600">
                      {pdfExportMode
                        ? formatDateTimeUtcFromTimestamp(tx.timestamp)
                        : formatDateFromTimestamp(tx.timestamp)}
                    </td>
                  </tr>
                  {showDetails && (
                    <FifoDetailRow 
                      tx={tx} 
                      inflows={tx.fifoLegs || tx.matchedInflows || []}
                      isEditMode={isEditMode}
                      nonTaxableInflows={{}}
                      onNonTaxableChange={() => {}}
                      selectedCountry={selectedCountry}
                      isPreviewMode={isPreviewMode}
                      maskRowEur={maskRowEur}
                      pdfExportMode={pdfExportMode}
                      language={language}
                    />
                  )}
                  <PreviewModeSeparator
                    show={shouldShowPreviewSeparatorAfterRow(index, sorted.length, isPreviewMode)}
                    colSpan={8}
                    language={language}
                    currencyCode={currencyCode}
                  />
                </>
              );

              if (pdfExportMode && !virtualMode) {
                return (
                  <tbody key={rowKey} className="pdf-row-group divide-y divide-gray-100">
                    {rowContent}
                  </tbody>
                );
              }

              return (
                <React.Fragment key={rowKey}>
                  {rowContent}
                </React.Fragment>
              );
  };

  return (
    <section
      className={cn(
        T.shell,
        "report-section",
        pdfExportMode && "rounded-none border-0 bg-white shadow-none ring-0"
      )}
    >
      <div className={cn(T.header, T.headerRail, tone.rail)}>
        <div className={T.headerInner}>
          <div className={T.headerMain}>
            <span className={T.eyebrow}>
              <LayoutDashboard className={cn("h-3.5 w-3.5", tone.eyebrow)} />
              {t("taxYear", language)} {yearLabel}
            </span>
            <h2 className={T.title}>
              {t("vip180", language)} {t("taxTransactions", language)}
            </h2>
            <p className={T.subtitle}>
              {language === "en"
                ? "Calculated VIP-180 token disposals and taxable income for the selected tax year"
                : "Berechnete VIP-180 Token-Veräußerungen und steuerpflichtige Einkünfte für das ausgewählte Steuerjahr"}
            </p>
          </div>
          {!pdfExportMode && (
            <div className={T.headerAsidePills}>
              <span className={cn(T.headerChipSoft, "text-indigo-700 border-indigo-200 bg-indigo-50/80")}>
                <span className="h-2 w-2 rounded-full bg-indigo-500" />
                <span>VIP-180</span>
              </span>

              <HeaderIconBadge>
                <img
                  src="/b3tr-icon-aa.png"
                  alt="VIP-180"
                  className="h-8 w-8 object-contain"
                  draggable={false}
                />
              </HeaderIconBadge>
            </div>
          )}
        </div>
      </div>

      {!pdfExportMode && (
        <div className={T.toolbarWrap}>
          <div className={cn(T.toolbar, "flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between")}>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                placeholder={`🔍 ${t("search", language)} ${t("type", language)}, ${t("transactionHash", language)}, ${t("amount", language)}, ${pdfExportMode ? (language === "en" ? "Date (UTC)" : "Datum (UTC)") : t("date", language)}...`}
                className={T.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className={T.countPill}>
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="font-semibold text-slate-800">{sorted.length}</span>
                <span>{t("transactions", language)}</span>
              </span>
              <select className={T.select} value={tokenFilter} onChange={(e) => setTokenFilter(e.target.value)}>
                <option value="ALL">🔁 {t("allTokens", language)}</option>
                {availableTokens.map((token) => (
                  <option key={token} value={token}>
                    {token}
                  </option>
                ))}
              </select>
              <select className={T.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="ALL">🔎 {t("allTypes", language)}</option>
                <option value="swap_out">🔄 Swap_out</option>
                {JUICY_TAX_TYPE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                <option value="x2earn_reward_claim">X2Earn Reward · Claim</option>
                <option value="governance_reward">🏛️ Governance Reward</option>
                <option value="vexchange_farm_rewards">🟦 Vexchange Farm Rewards</option>
                <option value="safehaven_staking_rewards">🛡️ Safehaven Staking Rewards</option>
                <option value="sagaz_reward_claim">🟦 Sagaz Reward Claim</option>
                <option value="vaiyans_vco_game_entry">🔮 Vaiyans VCO Game Entry</option>
                <option value="scoopup_puppy_pass_fee">ScoopUp · Puppy Pass</option>
                <option value="velottery_ticket_purchase">VeLottery · Paid Ticket</option>
                <option value="squad_game_spend">🐢 Squirtle Squad Game Spend</option>
                <option value="squad_nft_upgrade_spend">🐢 Squirtle Squad NFT Upgrade</option>
                <option value="squad_game_reward">🐢 Squirtle Squad Game Reward</option>
                <option value="vfox_nft_holder_reward">🦊 VFA NFT Holder Reward</option>
                <option value="vesea_nft_staking_rewards">🌊 VeSea NFT Staking Rewards</option>
                <option value="union_nft_holder_rewards">💊 UNION NFT Holder Rewards</option>
                <option value="vimworld_veed_reward_distribution">🟦 VIMworld Reward Distribution</option>
                <option value="betterswap_pool2earn_rewards">🏊 BetterSwap Pool2Earn Rewards</option>
                <option value="mva_staking_rewards">🎯 MVA Staking Rewards</option>
                <option value="listen2earn_rewards">🎧 Listen2Earn Rewards</option>
                <option value="wanchain_bridge_to_earn_reward">Wanchain Bridge-to-Earn Reward</option>
                <option value="bangzboardz_staking_reward">BangzBoardz Staking Reward</option>
                <option value="goatz_milk_nft_holder_reward">Goatz Club · MILK NFT Holder Reward</option>
                <option value="b3mo">B3MO</option>
                <option value="b3mo_challenge_escrow_deposit">B3MO · Challenge Deposit</option>
                <option value="b3mo_challenge_principal_return">B3MO · Principal Return</option>
                <option value="b3mo_challenge_payout_reward">B3MO · Challenge Reward</option>
                <option value="b3mo_split_win_reward">B3MO · SplitWin Reward</option>
                <option value="genesis_staking_rewards">🧬 Genesis Staking Rewards</option>
                <option value="wov_genesis_staking_rewards">🧬 World of V Staking Rewards</option>
                <option value="wov_staking_rewards">🧬 World of V Staking Rewards (Legacy)</option>
                <option value="golden_empire_reward">🏰 Golden Empire Rewards</option>
                <option value="golden_empire_spend">🏰 Golden Empire Spend</option>
                <option value="golden_empire_burn_spend">🏰 Golden Empire Burn Spend</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <PreviewTableNotice
        isPreviewMode={isPreviewMode}
        totalRows={sorted.length}
        language={language}
        currencyCode={currencyCode}
      />

      <div className={cn(T.tableBlock, "hidden md:block print:block")}>
        <div className={cn(T.tableWrap, primaryTableWrapClass)}>
        {sorted.length === 0 ? (
          <table className={cn(T.table, "min-w-[1200px] table-fixed print:min-w-0 print:table-fixed")}>
            {renderDesktopColgroup()}
            {renderDesktopThead()}
            <tbody className={T.tbody}>
              <tr>
                <td colSpan={8} className="px-6 py-10">
                  {!pdfExportMode ? (
                    <UnifiedTableEmptyState
                      language={language}
                      year={yearLabel}
                      scopeLabel="VIP-180"
                      totalCount={emptyMetrics.totalCount}
                      yearCount={emptyMetrics.yearCount}
                      otherYearsCount={emptyMetrics.otherYearsCount}
                      hiddenByFilterCount={emptyMetrics.hiddenByFilterCount}
                      inTable
                    />
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table className={cn(T.table, "min-w-[1200px] table-fixed print:min-w-0 print:table-fixed")}>
            {renderDesktopColgroup()}
            {renderDesktopThead()}
            {/* PDF mode emits one tbody per row block; otherwise the rows need
                a tbody of their own instead of sitting directly in the table. */}
            {pdfExportMode ? (
              sorted.map((tx, index) => renderB3trDesktopBlock(tx, index))
            ) : (
              <tbody className={T.tbody}>
                {sorted.map((tx, index) => renderB3trDesktopBlock(tx, index))}
              </tbody>
            )}
            <tbody className={T.tbody}>{renderDesktopTableFooter()}</tbody>
          </table>
        )}
        </div>
      </div>
    </section>
  );
}
